import React, { useState, useEffect } from 'react'
import { useDarkMode } from '../App'
import { BOOK_MANAGEMENT_API_BASE } from '../lib/api'

interface Book {
  slug: string
  title: string
  authors: string[]
  year: number
  license: string
  type: 'book' | 'paper'
  coverUrl: string
  fileSize: string
  fileType: string
  description: string
}

interface BookEditModalProps {
  book: Book | null
  isOpen: boolean
  onClose: () => void
  onSave: (updatedBook: Book) => void
  onError: (error: string) => void
}

export function BookEditModal({ book, isOpen, onClose, onSave, onError }: BookEditModalProps) {
  const { isDark } = useDarkMode()
  const [formData, setFormData] = useState<Book | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (book) {
      setFormData({ ...book })
    }
  }, [book])

  if (!isOpen || !book || !formData) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await fetch(`${BOOK_MANAGEMENT_API_BASE}/api/update-book/${book.slug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update book')
      }

      const result = await response.json()
      onSave(result.updated_book)
      onClose()
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Update failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAuthorsChange = (index: number, value: string) => {
    if (!formData) return
    const newAuthors = [...formData.authors]
    newAuthors[index] = value
    setFormData({ ...formData, authors: newAuthors })
  }

  const addAuthor = () => {
    if (!formData) return
    setFormData({ ...formData, authors: [...formData.authors, ''] })
  }

  const removeAuthor = (index: number) => {
    if (!formData || formData.authors.length <= 1) return
    const newAuthors = formData.authors.filter((_, i) => i !== index)
    setFormData({ ...formData, authors: newAuthors })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto rounded-lg ${
        isDark ? 'bg-gray-800' : 'bg-white'
      }`}>
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-2xl font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Edit Book: {book.title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className={`text-gray-500 hover:text-gray-700 ${
                isDark ? 'hover:text-gray-300' : ''
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={`w-full px-3 py-2 border rounded-md ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                required
              />
            </div>

            {/* Slug */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Slug *
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className={`w-full px-3 py-2 border rounded-md ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                required
              />
              <p className={`text-xs mt-1 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Warning: Changing slug will require file renaming
              </p>
            </div>

            {/* Year */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Year *
              </label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                className={`w-full px-3 py-2 border rounded-md ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                min="1000"
                max="2100"
                required
              />
            </div>
          </div>

          {/* Authors */}
          <div className="mt-6">
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Authors *
            </label>
            {formData.authors.map((author, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={author}
                  onChange={(e) => handleAuthorsChange(index, e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-md ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder={`Author ${index + 1}`}
                  required
                />
                {formData.authors.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeAuthor(index)}
                    className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addAuthor}
              className="text-blue-500 hover:text-blue-700 text-sm"
            >
              + Add another author
            </button>
          </div>

          {/* License, Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                License
              </label>
              <select
                value={formData.license}
                onChange={(e) => setFormData({ ...formData, license: e.target.value })}
                className={`w-full px-3 py-2 border rounded-md ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="CC-BY">CC-BY</option>
                <option value="CC-BY-SA">CC-BY-SA</option>
                <option value="CC-BY-NC">CC-BY-NC</option>
                <option value="Public Domain">Public Domain</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'book' | 'paper' })}
                className={`w-full px-3 py-2 border rounded-md ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="book">Book</option>
                <option value="paper">Paper</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="mt-6">
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Brief description of the book/paper..."
            />
          </div>

          {/* File Info (Read-only) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                File Size
              </label>
              <input
                type="text"
                value={formData.fileSize}
                readOnly
                className={`w-full px-3 py-2 border rounded-md bg-gray-100 ${
                  isDark 
                    ? 'bg-gray-600 border-gray-600 text-gray-300' 
                    : 'bg-gray-100 border-gray-300 text-gray-600'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Cover URL
              </label>
              <input
                type="text"
                value={formData.coverUrl}
                readOnly
                className={`w-full px-3 py-2 border rounded-md bg-gray-100 ${
                  isDark 
                    ? 'bg-gray-600 border-gray-600 text-gray-300' 
                    : 'bg-gray-100 border-gray-300 text-gray-600'
                }`}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-md border ${
                isDark 
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 rounded-md text-white ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              {isLoading ? 'Updating...' : 'Update Book'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}